import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-label", "accessibilityLabel", "altText", "amount", "backgroundColorToken", "backgroundColorTokenArgs", "borderColorToken", "borderColorTokenArgs", "cornerRadiusToken", "disabled", "fallbackCharacter", "hasBorder", "icon", "id", "imgResizeMode", "large", "loading", "medium", "role", "size", "small", "textColorToken", "textColorTokenArgs", "source", "square", "onError", "testID", "UNSAFE_containerProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Text } from '@bubble-ui/primitive-text';
import { useDesignContext } from '@klarna/designtokens';
import { Loader } from '@bubble-ui/molecule-loader';
import { View } from '@bubble-ui/primitive-view';
import { isNative } from '@bubble-ui/util-platform';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { usePropValueDeprecationWarning } from '@bubble-ui/hook-use-prop-value-deprecation-warning';
import { usePropDeprecationWarning } from '@bubble-ui/hook-use-prop-deprecation-warning';
import { defaultComponentTokens } from './defaultComponentTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { AvatarImage } from './AvatarImage';
var Avatar = _ref => {
  var {
      'aria-label': ariaLabel,
      accessibilityLabel: ariaLabelDeprecated,
      altText,
      amount,
      backgroundColorToken,
      backgroundColorTokenArgs,
      borderColorToken,
      borderColorTokenArgs,
      cornerRadiusToken,
      disabled,
      fallbackCharacter,
      hasBorder = true,
      icon: Icon,
      id,
      imgResizeMode = 'contain',
      large,
      loading,
      medium,
      role,
      size,
      small,
      textColorToken,
      textColorTokenArgs,
      source,
      square,
      onError,
      testID,
      UNSAFE_containerProps
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'Avatar',
    props,
    useInstead: 'UNSAFE_containerProps',
    removalDate: 'v7.0.0'
  });
  var isIconComponent = Boolean(Icon && ! /*#__PURE__*/React.isValidElement(Icon));
  var iconComponentName = 'SomeIcon';
  usePropValueDeprecationWarning({
    componentName: 'Avatar',
    propName: 'icon',
    propValue: "{".concat(iconComponentName, "}"),
    shouldWarn: isIconComponent,
    useInstead: "<".concat(iconComponentName, "/>"),
    removalDate: 'v8.0.0'
  });
  usePropDeprecationWarning({
    componentName: 'Avatar',
    propName: 'small',
    shouldWarn: small !== undefined,
    useInstead: 'size={32}',
    removalDate: 'v7.0.0'
  });
  usePropDeprecationWarning({
    componentName: 'Avatar',
    propName: 'medium',
    shouldWarn: medium !== undefined,
    useInstead: 'size={40}',
    removalDate: 'v7.0.0'
  });
  usePropDeprecationWarning({
    componentName: 'Avatar',
    propName: 'large',
    shouldWarn: large !== undefined,
    useInstead: 'size={72}',
    removalDate: 'v7.0.0'
  });
  var [hasSourceError, setHasSourceError] = useState(false);
  useEffect(() => {
    setHasSourceError(false);
  }, [source]);
  var designContext = useDesignContext(defaultComponentTokens);
  var sizeArg = small ? 'small' : medium ? 'medium' : large ? 'large' : 'default';
  var avatarSize = size || designContext.get('avatar/image-size', {
    size: sizeArg
  }).value();
  var loaderStrokeWidth = avatarSize >= 72 ? 3 : avatarSize <= 32 ? 1 : 2;
  var loaderSpacing = 2;
  var loaderSize = avatarSize + 2 * loaderSpacing + 2 * loaderStrokeWidth;
  var loaderMargin = (avatarSize - loaderSize) / 2;
  var {
    amountBackgroundStyle,
    amountTextStyle,
    borderStyle,
    hasSourceStyle,
    iconViewStyle,
    imgStyle,
    textStyle,
    viewStyle
  } = getDefaultStyleSheet({
    avatarImageSize: avatarSize,
    backgroundColorToken,
    backgroundColorTokenArgs,
    borderColorToken,
    borderColorTokenArgs,
    cornerRadiusToken,
    designContext,
    disabled,
    imgResizeMode,
    large,
    medium,
    size,
    small,
    square,
    textColorToken,
    textColorTokenArgs
  });
  return /*#__PURE__*/React.createElement(View, null, /*#__PURE__*/React.createElement(View, _extends({
    id: id,
    testID: testID
  }, UNSAFE_containerProps, {
    accessible: Boolean(ariaLabel || ariaLabelDeprecated || role) || undefined,
    "aria-label": ariaLabel || ariaLabelDeprecated,
    accessibilityRole: role,
    role: role,
    style: viewStyle
  }, props), fallbackCharacter && (!source || hasSourceError) && !Icon && /*#__PURE__*/React.createElement(Text, {
    style: textStyle
  }, fallbackCharacter.toUpperCase()), Icon && (!source || hasSourceError) && /*#__PURE__*/React.createElement(View, {
    style: iconViewStyle
  }, /*#__PURE__*/React.isValidElement(Icon) ? Icon : /*#__PURE__*/React.createElement(Icon, null)), source !== undefined && Boolean(source) && !hasSourceError && /*#__PURE__*/React.createElement(AvatarImage, {
    avatarImageSize: avatarSize,
    altText: altText,
    source: source,
    imgResizeMode: imgResizeMode,
    imgStyle: imgStyle,
    hasSourceStyle: hasSourceStyle,
    onError: () => {
      onError && onError();
      setHasSourceError(true);
    }
  }), (borderColorToken || hasBorder) && /*#__PURE__*/React.createElement(View, {
    style: _objectSpread(_objectSpread({}, !isNative ? hasSourceStyle : {}), borderStyle)
  }), amount && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: amountBackgroundStyle
  }), /*#__PURE__*/React.createElement(Text, {
    style: amountTextStyle
  }, amount))), loading && /*#__PURE__*/React.createElement(View, {
    style: {
      marginBottom: loaderMargin,
      marginLeft: loaderMargin,
      marginRight: loaderMargin,
      marginTop: loaderMargin - avatarSize
    }
  }, /*#__PURE__*/React.createElement(Loader, {
    UNSAFE_size: loaderSize,
    UNSAFE_strokeWidth: loaderStrokeWidth
  })));
};
Avatar.displayName = 'Avatar';
export { Avatar };
//# sourceMappingURL=index.js.map